import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FaBars, FaTrash, FaDownload, FaBookOpen, FaHeadphones, FaImage,
  FaRoute, FaSearch, FaMap, FaMapMarkerAlt, FaFont, FaCheckCircle, FaMapMarkedAlt,
} from "react-icons/fa";
import * as db from "../cache/db";
import {
  getCachedRoutes, getFullCachedRoute, warmRouteTiles, clearRouteTilesFlag, cleanupLegacyPmtiles, ROUTE_INDEX_KEY,
} from "../cache/routePreCache";
import {
  computeStorageBreakdown, formatBytes, KEY_FILTERS, CACHE_NAMES,
} from "../utils/storageUsage";
import "../styles/OfflineContentScreen.css";

const CATEGORY_ROWS = [
  { id: "stories",     icon: FaBookOpen,     translationKey: "cat_stories" },
  { id: "audio",       icon: FaHeadphones,   translationKey: "cat_audio" },
  { id: "photos",      icon: FaImage,        translationKey: "cat_photos" },
  { id: "routes",      icon: FaRoute,        translationKey: "cat_routes", warn: true },
  { id: "searches",    icon: FaSearch,       translationKey: "cat_searches" },
  { id: "mapTiles",    icon: FaMap,          translationKey: "cat_map_tiles" },
  { id: "geocoder",    icon: FaMapMarkerAlt, translationKey: "cat_geocoder" },
  { id: "placePhotos", icon: FaImage,        translationKey: "cat_place_photos" },
  { id: "fonts",       icon: FaFont,         translationKey: "cat_fonts" },
];

async function deleteCategory(id) {
  switch (id) {
    case "stories":     return db.delByPredicate(KEY_FILTERS.stories);
    case "audio":       return db.delByPredicate(KEY_FILTERS.audio);
    case "photos":      return db.delByPredicate(KEY_FILTERS.photos);
    case "searches":    return db.delByPredicate(KEY_FILTERS.searches);
    case "routes":
      await db.delByPredicate(KEY_FILTERS.routes);
      await db.del(ROUTE_INDEX_KEY);
      return;
    case "mapTiles":    return caches.delete(CACHE_NAMES.mapTiles);
    case "geocoder":    return caches.delete(CACHE_NAMES.geocoder);
    case "placePhotos": return caches.delete(CACHE_NAMES.placePhotos);
    case "fonts":       return caches.delete(CACHE_NAMES.fonts);
    default: return;
  }
}

export default function OfflineContentScreen({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [breakdown, setBreakdown] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [busy, setBusy] = useState(null);            // category id mid-delete
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ done: 0, total: 0 });

  const refresh = useCallback(async () => {
    const [b, r] = await Promise.all([
      computeStorageBreakdown(),
      getCachedRoutes(),
    ]);
    setBreakdown(b);
    setRoutes([...r].reverse());
  }, []);

  useEffect(() => {
    cleanupLegacyPmtiles().catch(() => {});
    refresh().catch(() => {});
  }, [refresh]);

  const handleDeleteCategory = async (id, warn) => {
    const name = t(`offline_content.${CATEGORY_ROWS.find(c => c.id === id).translationKey}`);
    const msg = warn
      ? `${t("offline_content.delete_confirm", { name })}\n\n${t("offline_content.routes_warning")}`
      : t("offline_content.delete_confirm", { name });
    if (!window.confirm(msg)) return;
    setBusy(id);
    try { await deleteCategory(id); } catch (e) { console.error(e); }
    setBusy(null);
    refresh();
  };

  const handleDownloadMap = async (route) => {
    if (!route.bbox) return;
    setDownloadingId(route.id);
    setDownloadProgress({ done: 0, total: 0 });
    try {
      await warmRouteTiles(route.id, route.bbox, (done, total) => {
        setDownloadProgress({ done, total });
      });
    } catch (err) {
      console.error("Tile warming failed", err);
    } finally {
      setDownloadingId(null);
      setDownloadProgress({ done: 0, total: 0 });
      refresh();
    }
  };

  const handleDeleteMap = async (routeId) => {
    await clearRouteTilesFlag(routeId);
    refresh();
  };

  const handleViewOnMap = async (routeId) => {
    const cached = await getFullCachedRoute(routeId);
    if (!cached) return;
    navigate("/homepage", { state: { cachedRoute: cached } });
  };

  const total = breakdown?.total ?? 0;
  const quota = breakdown?.quota ?? null;
  const pct = quota ? Math.min(100, (total / quota) * 100) : 0;

  return (
    <div className="ocs-container">
      <div className="ocs-header">
        <div>
          <h1 className="ocs-title">{t("offline_content.title")}</h1>
          <p className="ocs-subtitle">{t("offline_content.subtitle")}</p>
        </div>
        <button className="ocs-menu-btn" onClick={() => navigate("/homepage")}>
          <FaBars />
        </button>
      </div>

      <div className="ocs-summary">
        <div className="ocs-summary-row">
          <span className="ocs-summary-label">{t("offline_content.total")}</span>
          <span className="ocs-summary-value">
            {breakdown
              ? (quota
                  ? t("offline_content.of_quota", { used: formatBytes(total), quota: formatBytes(quota) })
                  : formatBytes(total))
              : "…"}
          </span>
        </div>
        {quota && (
          <div className="ocs-progress">
            <div className="ocs-progress-fill" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>

      <div className="ocs-section">
        {CATEGORY_ROWS.map(({ id, icon: Icon, translationKey, warn }) => {
          const bytes = breakdown?.[id] ?? null;
          const empty = bytes === 0;
          return (
            <div key={id} className={`ocs-row${empty ? " ocs-row--empty" : ""}`}>
              <Icon className="ocs-row-icon" />
              <span className="ocs-row-label">{t(`offline_content.${translationKey}`)}</span>
              <span className="ocs-row-size">{bytes == null ? "…" : formatBytes(bytes)}</span>
              <button
                className="ocs-row-delete"
                onClick={() => handleDeleteCategory(id, warn)}
                disabled={busy === id || empty}
                title={t("offline_content.delete")}
              >
                <FaTrash />
              </button>
            </div>
          );
        })}
      </div>

      <div className="ocs-section">
        <div className="ocs-section-header">
          <h2 className="ocs-section-title">{t("offline_content.maps_title")}</h2>
          <p className="ocs-section-subtitle">{t("offline_content.maps_subtitle")}</p>
        </div>

        {routes.length === 0 ? (
          <p className="ocs-empty">{t("offline_content.maps_empty")}</p>
        ) : routes.map(r => {
          const title = r.name
            || (r.originLabel && r.destLabel ? `${r.originLabel} → ${r.destLabel}` : null)
            || t("offline_routes.unnamed");
          const downloaded = Boolean(r.tilesDownloaded);
          const isDownloading = downloadingId === r.id;
          return (
            <div key={r.id} className="ocs-map-row">
              <span className="ocs-map-title">{title}</span>
              {downloaded && (
                <span className="ocs-map-badge">
                  <FaCheckCircle /> {t("offline_content.tiles_downloaded", { count: r.tileCount ?? 0 })}
                </span>
              )}
              {isDownloading && (
                <span className="ocs-map-progress">
                  {t("offline_content.downloading", { done: downloadProgress.done, total: downloadProgress.total })}
                </span>
              )}
              <button
                className="ocs-row-view"
                onClick={() => handleViewOnMap(r.id)}
                title={t("offline_content.view_on_map")}
                aria-label={t("offline_content.view_on_map")}
              >
                <FaMapMarkedAlt />
              </button>
              {downloaded ? (
                <button className="ocs-row-delete" onClick={() => handleDeleteMap(r.id)} title={t("offline_content.delete")}>
                  <FaTrash />
                </button>
              ) : (
                <button
                  className="ocs-row-download"
                  onClick={() => handleDownloadMap(r)}
                  disabled={isDownloading || !r.bbox}
                >
                  <FaDownload /> {t("offline_content.download")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
